<template>
  <div
    v-if="isVisible && isValid"
    class="confetti-container"
    :style="`--floor-height: ${stageHeight}px;`"
  >
    <div
      v-for="{ color, degree } in particles"
      :key="degree"
      :ref="el => setItemRef(el, degree)"
      class="particle"
    >
      <div :style="`--bgcolor: ${color};`" />
    </div>
  </div>
</template>

<script>
const ROTATION_SPEED_MIN = 200
const ROTATION_SPEED_MAX = 800
const CRAZY_PARTICLES_FREQUENCY = 0.1
const CRAZY_PARTICLE_CRAZINESS = 0.3
const BEZIER_MEDIAN = 0.5
const FORCE = 0.5
const SIZE = 12
const FLOOR_HEIGHT = 1600
const FLOOR_WIDTH = 1600
const PARTICLE_COUNT = 150
const DURATION = 3500
const COLORS = ['#FFC700', '#FF0000', '#2E3191', '#41BBC7']

export default {
  name: 'BaseConfettiExplosion',

  props: {
    particleCount: {
      type: Number,
      default: PARTICLE_COUNT
    },
    particleSize: {
      type: Number,
      default: SIZE
    },
    duration: {
      type: Number,
      default: DURATION
    },
    colors: {
      type: Array,
      default: () => COLORS
    },
    force: {
      type: Number,
      default: FORCE
    },
    stageHeight: {
      type: Number,
      default: FLOOR_HEIGHT
    },
    stageWidth: {
      type: Number,
      default: FLOOR_WIDTH
    },
    shouldDestroyAfterDone: {
      type: Boolean,
      default: true
    }
  },

  data: () => ({
    isVisible: true,
    zAxisRotation: [0, 0, 1],
    rotationTransforms: [
      [1, 1, 0],
      [1, 0, 1],
      [0, 1, 1],
      [1, 0, 0],
      [0, 1, 0],
      [0, 0, 1]
    ]
  }),

  computed: {
    particles() {
      return this.createParticles(this.particleCount, this.colors)
    },
    isValid() {
      return this.validate(
        this.particleCount,
        this.duration,
        this.colors,
        this.particleSize,
        this.force,
        this.stageHeight,
        this.stageWidth
      )
    }
  },

  mounted() {
    this.waitFor(this.duration).then(() => {
      if (this.shouldDestroyAfterDone) {
        this.isVisible = false
      }
    })

    if (this.particleCount > 300) {
      console.log(
        "[VUE-CONFETTI-EXPLOSION] That's a lot of confetti, you sure about that? A lesser number" +
          ' like 200 will still give off the party vibes while still not bricking the device 😉'
      )
    }
  },

  methods: {
    setItemRef(el, degree) {
      this.confettiStyles(el, { degree })
    },
    createParticles(count, colors) {
      const increment = 360 / count
      return Array.from({ length: count }, (_, i) => ({
        color: colors[i % colors.length],
        degree: i * increment
      }))
    },
    waitFor(ms) {
      return new Promise(resolve => setTimeout(resolve, ms))
    },
    round(num, precision = 2) {
      return (
        Math.round((num + Number.EPSILON) * 10 ** precision) / 10 ** precision
      )
    },
    arraysEqual(a, b) {
      if (a === b) return true
      if (a == null || b == null) return false
      if (a.length !== b.length) return false

      for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false

      return true
    },
    mapRange(value, x1, y1, x2, y2) {
      return ((value - x1) * (y2 - x2)) / (y1 - x1) + x2
    },
    rotate(degree, amount) {
      const result = degree + amount
      return result > 360 ? result - 360 : result
    },
    coinFlip() {
      return Math.random() > 0.5
    },
    shouldBeCircle(rotationIndex) {
      return (
        !this.arraysEqual(
          this.rotationTransforms[rotationIndex],
          this.zAxisRotation
        ) && this.coinFlip()
      )
    },
    isUndefined(value) {
      return typeof value === 'undefined'
    },
    error(message) {
      console.error(message)
    },
    validate(
      particleCount,
      duration,
      colors,
      particleSize,
      force,
      floorHeight,
      floorWidth
    ) {
      const isSafeInteger = Number.isSafeInteger
      if (
        !this.isUndefined(particleCount) &&
        isSafeInteger(particleCount) &&
        particleCount < 0
      ) {
        this.error('particleCount must be a positive integer')
        return false
      }

      if (
        !this.isUndefined(duration) &&
        isSafeInteger(duration) &&
        duration < 0
      ) {
        this.error('duration must be a positive integer')
        return false
      }

      if (!this.isUndefined(colors) && !Array.isArray(colors)) {
        this.error('colors must be an array of strings')
        return false
      }

      if (
        !this.isUndefined(particleSize) &&
        isSafeInteger(particleSize) &&
        particleSize < 0
      ) {
        this.error('particleSize must be a positive integer')
        return false
      }

      if (
        !this.isUndefined(force) &&
        isSafeInteger(force) &&
        (force < 0 || force > 1)
      ) {
        this.error(
          'force must be a positive integer and should be within 0 and 1'
        )
        return false
      }

      if (
        !this.isUndefined(floorHeight) &&
        typeof floorHeight === 'number' &&
        isSafeInteger(floorHeight) &&
        floorHeight < 0
      ) {
        this.error('floorHeight must be a positive integer')
        return false
      }

      if (
        !this.isUndefined(floorWidth) &&
        typeof floorWidth === 'number' &&
        isSafeInteger(floorWidth) &&
        floorWidth < 0
      ) {
        this.error('floorWidth must be a positive integer')
        return false
      }

      return true
    },
    confettiStyles(node, { degree }) {
      const landingPoint = this.mapRange(
        Math.abs(this.rotate(degree, 90) - 180),
        0,
        180,
        -this.stageWidth / 2,
        this.stageWidth / 2
      )

      const rotation =
        Math.random() * (ROTATION_SPEED_MAX - ROTATION_SPEED_MIN) +
        ROTATION_SPEED_MIN
      const rotationIndex = Math.round(
        Math.random() * (this.rotationTransforms.length - 1)
      )
      const durationChaos = this.duration - Math.round(Math.random() * 1000)
      const shouldBeCrazy = Math.random() < CRAZY_PARTICLES_FREQUENCY
      const isCircle = this.shouldBeCircle(rotationIndex)

      const x1 = shouldBeCrazy
        ? this.round(Math.random() * CRAZY_PARTICLE_CRAZINESS, 2)
        : 0
      const x2 = x1 * -1
      const x3 = x1
      const x4 = this.round(
        Math.abs(
          this.mapRange(Math.abs(this.rotate(degree, 90) - 180), 0, 180, -1, 1)
        ),
        4
      )

      const y1 = this.round(Math.random() * BEZIER_MEDIAN, 4)
      const y2 = this.round(
        Math.random() * this.force * (this.coinFlip() ? 1 : -1),
        4
      )
      const y3 = BEZIER_MEDIAN
      const y4 = this.round(
        Math.max(
          this.mapRange(
            Math.abs(degree - 180),
            0,
            180,
            this.force,
            -this.force
          ),
          0
        ),
        4
      )

      const setCSSVar = (key, val) => node?.style.setProperty(key, val + '')

      setCSSVar('--x-landing-point', `${landingPoint}px`)
      setCSSVar('--duration-chaos', `${durationChaos}ms`)
      setCSSVar('--x1', `${x1}`)
      setCSSVar('--x2', `${x2}`)
      setCSSVar('--x3', `${x3}`)
      setCSSVar('--x4', `${x4}`)
      setCSSVar('--y1', `${y1}`)
      setCSSVar('--y2', `${y2}`)
      setCSSVar('--y3', `${y3}`)
      setCSSVar('--y4', `${y4}`)
      setCSSVar(
        '--width',
        `${
          isCircle
            ? this.particleSize
            : Math.round(Math.random() * 4) + this.particleSize / 2
        }px`
      )
      setCSSVar(
        '--height',
        (isCircle
          ? this.particleSize
          : Math.round(Math.random() * 2) + this.particleSize) + 'px'
      )
      setCSSVar(
        '--rotation',
        `${this.rotationTransforms[rotationIndex].join()}`
      )
      setCSSVar('--rotation-duration', `${rotation}ms`)
      setCSSVar('--border-radius', `${isCircle ? '50%' : '0'}`)
    }
  }
}
</script>

<style scoped>
@keyframes y-axis {
  to {
    transform: translate3d(0, var(--floor-height), 0);
  }
}
@keyframes x-axis {
  to {
    transform: translate3d(var(--x-landing-point), 0, 0);
  }
}
@keyframes rotation {
  to {
    transform: rotate3d(var(--rotation), 360deg);
  }
}
.confetti-container {
  width: 0;
  height: 0;
  overflow: visible;
  position: relative;
  transform: translate3d(var(--x, 0), var(--y, 0), 0);
  z-index: 1200;
}
.confetti-container > .particle {
  animation: x-axis var(--duration-chaos) forwards
    cubic-bezier(var(--x1), var(--x2), var(--x3), var(--x4));
}
.confetti-container > .particle div {
  position: absolute;
  top: 0;
  left: 0;
  animation: y-axis var(--duration-chaos) forwards
    cubic-bezier(var(--y1), var(--y2), var(--y3), var(--y4));
  width: var(--width);
  height: var(--height);
}
.confetti-container > .particle div:before {
  display: block;
  height: 100%;
  width: 100%;
  content: '';
  background-color: var(--bgcolor);
  animation: rotation var(--rotation-duration) infinite linear;
  border-radius: var(--border-radius);
}
</style>
